/**
 * VariationPathUtils.js
 * Utilities for handling infinite nested variation paths in chess games
 * 
 * Path format examples:
 * - "4" (main line move 4)
 * - "4|0:1" (main move 4 → variation 0 → move 1)
 * - "4|0:1|1:2|0:1" (main move 4 → var 0 → move 1 → var 1 → move 2 → var 0 → move 1)
 */

/**
 * Represents a segment in a variation path
 * @typedef {Object} PathSegment
 * @property {string} type - 'main' or 'variation'
 * @property {number} index - For main: move index, for variation: variation index
 * @property {number} moveIndex - For variations: move index within variation
 */

/**
 * Parse a variation path string into structured segments
 * @param {string} path - Path string like "4|0:1|1:2" or "-1|0:1" for starting position variations
 * @returns {PathSegment[]} Array of path segments
 */
export function parseVariationPath(path) {
  
  
  if (!path) {
    return [];
  }
  
  // Handle starting position (with or without variations)
  if (path === '-1') {
    return [];
  }
  
  const segments = [];
  const parts = path.split('|');
  
  // First part is always a main line move index (can be -1 for starting position)
  const mainIndex = parseInt(parts[0], 10);
  if (isNaN(mainIndex)) {
    console.error('[VariationPathUtils] parseVariationPath: Invalid main index:', parts[0]);
    return [];
  }
  
  segments.push({
    type: 'main',
    index: mainIndex,
    moveIndex: null
  });
  
  // Process remaining parts as variations
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    
    if (!part.includes(':')) {
      console.error('[VariationPathUtils] parseVariationPath: Invalid variation format:', part);
      continue;
    }
    
    const [varIndex, moveIndex] = part.split(':').map(Number);
    
    if (isNaN(varIndex) || isNaN(moveIndex)) {
      console.error('[VariationPathUtils] parseVariationPath: Invalid variation indices:', part);
      continue;
    }
    
    segments.push({
      type: 'variation',
      index: varIndex,
      moveIndex: moveIndex
    });
  }
  
  
  return segments;
}

/**
 * Build a path string from segments array
 * @param {PathSegment[]} segments - Array of path segments
 * @returns {string} Path string
 */
export function buildVariationPath(segments) {
  if (!segments || segments.length === 0) {
    return '-1';
  }
  
  const pathParts = [];
  
  // First segment should be main
  if (segments[0].type === 'main') {
    pathParts.push(String(segments[0].index));
  } else {
    console.error('[VariationPathUtils] buildVariationPath: First segment must be main type');
    return '-1';
  }
  
  // Add variation segments
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    if (segment.type === 'variation') {
      pathParts.push(`${segment.index}:${segment.moveIndex}`);
    }
  }
  
  const result = pathParts.join('|');
  
  return result;
}

/**
 * Handle variations from starting position (index=-1)
 * @param {Object} gameData - Game data object containing startPosition
 * @param {PathSegment} segment - Current path segment
 * @param {PathSegment[]} remainingSegments - Remaining segments to process
 * @returns {Object} Navigation result
 */
function handleStartingPositionVariation(gameData, segment, remainingSegments) {
  // Get or create starting position with variations support
  const startPos = gameData.startPosition || getDefaultStartPosition();
  
  // Ensure variations array exists
  if (!startPos.variations) {
    startPos.variations = [];
  }
  
  // If no remaining segments, return the starting position
  if (remainingSegments.length === 0) {
    return {
      success: true,
      node: startPos,
      parentNode: null,
      nodeArray: [startPos],
      nodeIndex: -1,
      isStartPosition: true
    };
  }
  
  // Navigate to the specific variation
  const nextSegment = remainingSegments[0];
  if (nextSegment.type === 'variation') {
    if (nextSegment.index >= 0 && nextSegment.index < startPos.variations.length) {
      const variation = startPos.variations[nextSegment.index];
      
      // Check if move index is valid
      if (nextSegment.moveIndex >= 0 && nextSegment.moveIndex < variation.length) {
        const targetMove = variation[nextSegment.moveIndex];
        
        // If there are more segments, continue navigation
        if (remainingSegments.length > 1) {
          return processRemainingSegments(targetMove, variation, remainingSegments.slice(1));
        }
        
        return {
          success: true,
          node: targetMove,
          parentNode: startPos,
          nodeArray: variation,
          nodeIndex: nextSegment.moveIndex
        };
      }
    }
  }
  
  return {
    success: false,
    error: 'Invalid variation path from starting position',
    node: null,
    parentNode: null,
    nodeArray: null,
    nodeIndex: -1
  };
}

/**
 * Process remaining segments after handling starting position
 * @param {Object} currentNode - Current node
 * @param {Array} currentArray - Current array
 * @param {PathSegment[]} remainingSegments - Remaining segments
 * @returns {Object} Navigation result
 */
function processRemainingSegments(currentNode, currentArray, remainingSegments) {
  // For now, return the current node as we've reached the target
  // This can be extended for deeper nesting if needed
  return {
    success: true,
    node: currentNode,
    parentNode: null,
    nodeArray: currentArray,
    nodeIndex: -1
  };
}

/**
 * Get default starting position structure
 * @returns {Object} Default starting position
 */
function getDefaultStartPosition() {
  return {
    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    content: '',
    arrows: [],
    highlightedSquares: {},
    analysis: { pvs: [], scores: [], classification: '', depth: 0 },
    variations: []
  };
}

/**
 * Navigate to a node at any depth in the move tree
 * @param {Array} moves - Root moves array
 * @param {string} path - Path string
 * @param {Object} gameData - Game data object (optional, for starting position variations)
 * @returns {Object} Navigation result with node information
 */
export function getNodeAtPathInfinite(moves, path, gameData = null) {
  
  
  if (!moves || moves.length === 0) {
    
    return {
      success: false,
      error: 'No moves provided',
      node: null,
      parentNode: null,
      nodeArray: moves,
      nodeIndex: -1
    };
  }
  
  const segments = parseVariationPath(path);
  
  if (segments.length === 0) {
    
    return {
      success: true,
      node: null,
      parentNode: null,
      nodeArray: moves,
      nodeIndex: -1,
      isStartPosition: true
    };
  }
  
  // Handle starting position variations (index=-1)
  const firstSegment = segments[0];
  if (firstSegment.type === 'main' && firstSegment.index === -1) {
    if (gameData) {
      return handleStartingPositionVariation(gameData, firstSegment, segments.slice(1));
    } else {
      // No game data provided but path references starting position
      return {
        success: true,
        node: null,
        parentNode: null,
        nodeArray: moves,
        nodeIndex: -1,
        isStartPosition: true
      };
    }
  }
  
  let currentArray = moves;
  let currentNode = null;
  let parentNode = null;
  let parentArray = null;
  
  try {
    // Process each segment
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      
      if (segment.type === 'main') {
        // Main line move (allow -1 for starting position)
        if (segment.index < -1 || segment.index >= currentArray.length) {
          console.error('[VariationPathUtils] getNodeAtPathInfinite: Invalid main index:', segment.index);
          return {
            success: false,
            error: `Invalid main index: ${segment.index}`,
            node: null,
            parentNode: parentNode,
            nodeArray: currentArray,
            nodeIndex: segment.index
          };
        }
        
        // Special handling for starting position (index=-1)
        if (segment.index === -1) {
          // This case should have been handled earlier, but handle it here as fallback
          return {
            success: true,
            node: null,
            parentNode: null,
            nodeArray: currentArray,
            nodeIndex: -1,
            isStartPosition: true
          };
        }
        
        parentNode = currentNode;
        parentArray = currentArray;
        currentNode = currentArray[segment.index];
        
        
        
      } else if (segment.type === 'variation') {
        // Variation move
        if (!currentNode) {
          console.error('[VariationPathUtils] getNodeAtPathInfinite: No current node for variation');
          return {
            success: false,
            error: 'No current node for variation',
            node: null,
            parentNode: parentNode,
            nodeArray: currentArray,
            nodeIndex: -1
          };
        }
        
        if (!currentNode.variations || !Array.isArray(currentNode.variations)) {
          console.error('[VariationPathUtils] getNodeAtPathInfinite: Node has no variations');
          return {
            success: false,
            error: 'Node has no variations',
            node: null,
            parentNode: parentNode,
            nodeArray: currentArray,
            nodeIndex: -1
          };
        }
        
        if (segment.index < 0 || segment.index >= currentNode.variations.length) {
          console.error('[VariationPathUtils] getNodeAtPathInfinite: Invalid variation index:', segment.index);
          return {
            success: false,
            error: `Invalid variation index: ${segment.index}`,
            node: null,
            parentNode: parentNode,
            nodeArray: currentArray,
            nodeIndex: -1
          };
        }
        
        const variation = currentNode.variations[segment.index];
        
        if (segment.moveIndex < 0 || segment.moveIndex >= variation.length) {
          console.error('[VariationPathUtils] getNodeAtPathInfinite: Invalid move index in variation:', segment.moveIndex);
          return {
            success: false,
            error: `Invalid move index in variation: ${segment.moveIndex}`,
            node: null,
            parentNode: parentNode,
            nodeArray: currentArray,
            nodeIndex: -1
          };
        }
        
        // Update tracking variables
        parentNode = currentNode;
        parentArray = currentArray;
        currentArray = variation;
        currentNode = variation[segment.moveIndex];
        
        
      }
    }
    
    // Determine the final node index
    const lastSegment = segments[segments.length - 1];
    const finalNodeIndex = lastSegment.type === 'main' ? lastSegment.index : lastSegment.moveIndex;
    
    
    return {
      success: true,
      node: currentNode,
      parentNode: parentNode,
      nodeArray: currentArray,
      nodeIndex: finalNodeIndex,
      segments: segments,
      path: path
    };
    
  } catch (error) {
    console.error('[VariationPathUtils] getNodeAtPathInfinite: Exception:', error);
    return {
      success: false,
      error: error.message,
      node: null,
      parentNode: parentNode,
      nodeArray: currentArray,
      nodeIndex: -1
    };
  }
}

/**
 * Get the sequence of moves to reach a specific path
 * @param {Array} moves - Root moves array
 * @param {string} path - Target path
 * @param {string} initialFEN - Starting position FEN
 * @returns {Array} Array of move notations to reach the position
 */
export function getMoveSequenceToPath(moves, path, initialFEN = null) {
  
  
  if (!moves || moves.length === 0 || !path || path === '-1') {
    return [];
  }
  
  const segments = parseVariationPath(path);
  if (segments.length === 0) {
    return [];
  }
  
  const moveSequence = [];
  let currentArray = moves;
  
  try {
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      if (segment.type === 'main') {
        // Add main line moves up to and including this index
        for (let j = 0; j <= segment.index && j < currentArray.length; j++) {
          if (currentArray[j] && currentArray[j].notation) {
            moveSequence.push(currentArray[j].notation);
          }
        }
        
        // If this is not the last segment, we need to prepare for variation
        if (i < segments.length - 1) {
          const mainMove = currentArray[segment.index];
          if (!mainMove || !mainMove.variations) {
            console.error('[VariationPathUtils] getMoveSequenceToPath: Main move has no variations');
            break;
          }
        }
        
      } else if (segment.type === 'variation') {
        // Get the parent move from previous segment
        const prevSegment = segments[i - 1];
        let parentMove;
        
        if (prevSegment.type === 'main') {
          // Previous segment was main line
          parentMove = moves[prevSegment.index];
          currentArray = moves;
        } else {
          // Previous segment was also a variation - navigate to it using the path
          const parentPath = buildVariationPath(segments.slice(0, i));
          
          
          const parentResult = getNodeAtPathInfinite(moves, parentPath);
          if (!parentResult.success) {
            console.error('[VariationPathUtils] getMoveSequenceToPath: Failed to get parent for nested variation:', parentResult.error);
            break;
          }
          
          parentMove = parentResult.node;
          currentArray = parentResult.nodeArray;
        }
        
        if (!parentMove || !parentMove.variations || !parentMove.variations[segment.index]) {
          console.error('[VariationPathUtils] getMoveSequenceToPath: Invalid variation path - no variation at index', segment.index);
          break;
        }
        
        const variation = parentMove.variations[segment.index];
        
        // Add variation moves up to and including the target move
        for (let j = 0; j <= segment.moveIndex && j < variation.length; j++) {
          if (variation[j] && variation[j].notation) {
            moveSequence.push(variation[j].notation);
          }
        }
        
        // Update current array for potential further nesting
        currentArray = variation;
      }
    }
    
    
    return moveSequence;
    
  } catch (error) {
    console.error('[VariationPathUtils] getMoveSequenceToPath: Exception:', error);
    return [];
  }
}

/**
 * Get the parent path of a given path
 * @param {string} path - Current path
 * @returns {string} Parent path
 */
export function getParentPath(path) {
  if (!path || path === '-1') {
    return '-1';
  }
  
  const segments = parseVariationPath(path);
  if (segments.length <= 1) {
    return '-1';
  }
  
  // Remove the last segment
  const parentSegments = segments.slice(0, -1);
  return buildVariationPath(parentSegments);
}

/**
 * Get the next move path in sequence
 * @param {string} currentPath - Current path
 * @param {Array} moves - Moves array
 * @returns {string|null} Next path or null if at end
 */
export function getNextMovePath(currentPath, moves) {
  if (!moves || moves.length === 0) {
    return null;
  }
  
  if (currentPath === '-1') {
    return moves.length > 0 ? '0' : null;
  }
  
  const segments = parseVariationPath(currentPath);
  if (segments.length === 0) {
    return moves.length > 0 ? '0' : null;
  }
  
  const lastSegment = segments[segments.length - 1];
  
  if (lastSegment.type === 'main') {
    // Check if there's a next main line move
    if (lastSegment.index + 1 < moves.length) {
      const newSegments = [...segments];
      newSegments[newSegments.length - 1] = {
        ...lastSegment,
        index: lastSegment.index + 1
      };
      return buildVariationPath(newSegments);
    }
  } else if (lastSegment.type === 'variation') {
    // Find the current variation array
    const result = getNodeAtPathInfinite(moves, currentPath);
    if (result.success && result.nodeArray) {
      // Check if there's a next move in this variation
      if (lastSegment.moveIndex + 1 < result.nodeArray.length) {
        const newSegments = [...segments];
        newSegments[newSegments.length - 1] = {
          ...lastSegment,
          moveIndex: lastSegment.moveIndex + 1
        };
        return buildVariationPath(newSegments);
      }
    }
  }
  
  return null;
}

/**
 * Get the previous move path in sequence
 * @param {string} currentPath - Current path
 * @param {Array} moves - Moves array
 * @returns {string|null} Previous path or null if at start
 */
export function getPreviousMovePath(currentPath, moves) {
  if (!moves || moves.length === 0 || currentPath === '-1') {
    return null;
  }
  
  const segments = parseVariationPath(currentPath);
  if (segments.length === 0) {
    return null;
  }
  
  const lastSegment = segments[segments.length - 1];
  
  if (lastSegment.type === 'main') {
    if (lastSegment.index > 0) {
      const newSegments = [...segments];
      newSegments[newSegments.length - 1] = {
        ...lastSegment,
        index: lastSegment.index - 1
      };
      return buildVariationPath(newSegments);
    } else {
      return '-1';
    }
  } else if (lastSegment.type === 'variation') {
    if (lastSegment.moveIndex > 0) {
      const newSegments = [...segments];
      newSegments[newSegments.length - 1] = {
        ...lastSegment,
        moveIndex: lastSegment.moveIndex - 1
      };
      return buildVariationPath(newSegments);
    } else {
      // Go back to parent position
      return getParentPath(currentPath);
    }
  }
  
  return null;
}

/**
 * Check if a path is within a variation (not main line)
 * @param {string} path - Path to check
 * @returns {boolean} True if in variation
 */
export function isInVariation(path) {
  if (!path || path === '-1') {
    return false;
  }
  
  return path.includes('|');
}

/**
 * Get the depth level of a path (0 = main line, 1 = first variation, etc.)
 * @param {string} path - Path to check
 * @returns {number} Depth level
 */
export function getPathDepth(path) {
  if (!path || path === '-1') {
    return 0;
  }
  
  const segments = parseVariationPath(path);
  return segments.filter(segment => segment.type === 'variation').length;
}

/**
 * Validate that a path exists in the move tree
 * @param {string} path - Path to validate
 * @param {Array} moves - Root moves array
 * @returns {Object} Validation result
 */
export function validatePath(path, moves) {
  if (!path || path === '-1') {
    return { valid: true, error: null };
  }
  
  const result = getNodeAtPathInfinite(moves, path);
  return {
    valid: result.success,
    error: result.error || null,
    node: result.node
  };
}

/**
 * Calculate cumulative move index for nested variations
 * This function properly accounts for the full path through main line and all variations
 * @param {string} parentId - Path like "4|0:1|1:2" 
 * @returns {number} Cumulative move index (0-based)
 */
export function calculateCumulativeMoveIndex(parentId) {
  if (parentId === '-1') return -1; // Start position
  if (!parentId.includes('|')) return parseInt(parentId); // Simple main line move
  
  // Parse the full path and calculate cumulative moves
  const pathSegments = parseVariationPath(parentId);
  let cumulativeIndex = 0;
  
  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    
    if (segment.type === 'main') {
      // Add main line moves up to this point
      cumulativeIndex += segment.index + 1; // +1 because index is 0-based
    } else if (segment.type === 'variation') {
      // Add moves within this variation up to the current move index
      cumulativeIndex += segment.moveIndex + 1; // +1 because moveIndex is 0-based
    }
  }
  
  return cumulativeIndex - 1; // Return 0-based index for calculation
} 